import {
  computed,
  inject,
  Injectable,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { MenuItem } from '../models/menu-item.model';
import {
  LogoutAuthOptions,
  OidcSecurityService,
} from 'angular-auth-oidc-client';
import { environment } from 'src/environments/environment';
import { UserRole } from 'src/app/core/auth/user/enum/user-role.enum';
import { Role } from 'src/app/core/auth/user/models/role';

@Injectable({ providedIn: 'root' })
export class MenuService {
  private readonly oidcSecurityService = inject(OidcSecurityService);

  // Signal für die aktuelle Rolle
  private _role: WritableSignal<Role | null> = signal<Role | null>(null);

  /**
   * Setzt die aktuelle Rolle und aktualisiert automatisch die Menüitems.
   */
  setRole(role: Role | null): void {
    this._role.set(role);
  }

  /**
   * Signal mit den aktuellen Menüitems, basierend auf der Rolle.
   */
  readonly menuItems: Signal<MenuItem[]> = computed<MenuItem[]>(() => {
    const role = this._role();
    const items: MenuItem[] = [
      { label: 'Hauptmenü', icon: 'pi pi-home', items: [] },
    ];

    switch (role?.id ?? 0) {
      case UserRole.Admin:
        items[0].items = [
          {
            label: 'Lieferanten',
            routerLink: ['/vendors'],
            icon: 'pi pi-truck',
          },
          { label: 'Importe', routerLink: ['/imports'], icon: 'pi pi-upload' },
          {
            label: 'Anfragen',
            routerLink: ['/admin-requests'],
            icon: 'pi pi-inbox',
            badge: '0',
            badgeClass: 'badge',
          },
          {
            label: 'Systeme',
            routerLink: ['/admin-systems'],
            icon: 'pi pi-sitemap',
          },
          {
            label: 'Artikeldaten',
            routerLink: ['/admin-items'],
            icon: 'pi pi-receipt',
          },
          { label: 'Kunden', routerLink: ['/clients'], icon: 'pi pi-users' },
          { label: 'Benutzer', routerLink: ['/users'], icon: 'pi pi-key' }
        ];
        break;

      case UserRole.Vendor:
        items[0].items = [
          {
            label: 'Kunden',
            routerLink: ['/vendor-dashboard'],
            icon: 'pi pi-users',
          },
          { label: 'Importe', routerLink: ['/imports'], icon: 'pi pi-upload' },
          {
            label: 'Anfragen',
            routerLink: ['/vendor-requests'],
            icon: 'pi pi-inbox',
            badge: '0',
            badgeClass: 'badge',
          },
        ];
        break;

      case UserRole.Customer:
        items[0].items = [
          {
            label: 'Lieferanten',
            routerLink: ['/customer-dashboard'],
            icon: 'pi pi-home',
          },
          {
            label: 'Anfragen',
            routerLink: ['/customer-requests'],
            icon: 'pi pi-inbox',
            badge: '0',
            badgeClass: 'badge',
          },
          /* NECTA-6612 - Artikeldaten aufgrund performaceintensiver Abfrage vorerst aus Kundenmenü entfernt
          { 
              label: 'Artikeldaten',
            routerLink: ['/customer-items'],
            icon: 'pi pi-receipt',
          },*/
          { 
            label: 'Importe',
            routerLink: ['/imports'],
            icon: 'pi pi-upload'
          },
        ];
        break;

      case UserRole.SystemOwner:
         items[0].items = [
          {
            label: 'Systeme',
            routerLink: ['/admin-systems'],
            icon: 'pi pi-sitemap',
          },
          { 
            label: 'Kunden',
            routerLink: ['/clients'],
            icon: 'pi pi-users'
          },
          {
            label: 'Anfragen',
            routerLink: ['/admin-requests'],
            icon: 'pi pi-inbox',
            badge: '0',
            badgeClass: 'badge',
          },
          {
            label: 'Artikeldaten',
            routerLink: ['/admin-items'],
            icon: 'pi pi-receipt',
          },
        ];
        break;


      default:
        return [];
    }

    return items;
  });

  readonly userMenuItems: Signal<MenuItem[]> = computed<MenuItem[]>(() => {
    const role = this._role();
    const items: MenuItem[] = [
      { label: 'Benutzer', icon: 'pi pi-home', items: [] },
    ];

    switch (role?.id ?? 0) {
      case UserRole.Admin:
        items[0].items = [
          /* { label: 'Profil', routerLink: ['/coming-soon'], icon: 'pi pi-user' },
          { label: 'Einstellungen', routerLink: ['/coming-soon2'], icon: 'pi pi-cog' },*/
          {
            label: 'Hilfe',
            icon: 'pi pi-question-circle',
            command: () => {
              this.openNectaManual();
            },
          },
          {
            label: 'Abmelden',
            icon: 'pi pi-sign-out',
            command: () => {
              this.logout();
            },
          },
        ];
        break;

      case UserRole.Vendor:
        items[0].items = [
          {
            label: 'Profil',
            routerLink: ['/vendor-profile'],
            icon: 'pi pi-user',
          },
          /* { label: 'Einstellungen', routerLink: ['/coming-soon2'], icon: 'pi pi-cog' },*/
          {
            label: 'Hilfe',
            icon: 'pi pi-question-circle',
            command: () => {
              this.openNectaManual();
            },
          },
          {
            label: 'Abmelden',
            icon: 'pi pi-sign-out',
            command: () => {
              this.logout();
            },
          },
        ];
        break;

      case UserRole.Customer:
        items[0].items = [
          /*{ label: 'Profil', routerLink: ['/coming-soon'], icon: 'pi pi-user' },
          { label: 'Einstellungen', routerLink: ['/coming-soon2'], icon: 'pi pi-cog' },*/
          {
            label: 'Hilfe',
            icon: 'pi pi-question-circle',
            command: () => {
              this.openNectaManual();
            },
          },
          {
            label: 'Abmelden',
            icon: 'pi pi-sign-out',
            command: () => {
              this.logout();
            },
          },
        ];
        break;

      case UserRole.SystemOwner:
        items[0].items = [
          {
            label: 'Hilfe',
            icon: 'pi pi-question-circle',
            command: () => {
              this.openNectaManual();
            },
          },
          {
            label: 'Abmelden',
            icon: 'pi pi-sign-out',
            command: () => {
              this.logout();
            },
          },
        ];
        break;
      default:
         items[0].items = [
          {
            label: 'Abmelden',
            icon: 'pi pi-sign-out',
            command: () => {
              this.logout();
            },
          },
        ];
        break;
    }

    return items;
  });

  /**
   * Backward-kompatible Methode für Komponenten, die noch getMenuItems aufrufen.
   * Setzt die Rolle und liefert die aktuellen Menüitems zurück.
   */
  getMenuItems(role: Role | null): MenuItem[] {
    this.setRole(role);
    return this.menuItems();
  }

  logout(): void {
    var options: LogoutAuthOptions = {
      customParams: {
        state: 'local',
      },
    };
    this.oidcSecurityService
      .logoffAndRevokeTokens(undefined, options)
      .subscribe((res) =>
        console.debug('Logout und Token-Widerruf erfolgreich')
      );
  }

  /**
   * Backward-kompatible Methode für Komponenten, die noch getMenuItems aufrufen.
   * Setzt die Rolle und liefert die aktuellen Menüitems zurück.
   */
  getUserMenuItems(role: Role | null): MenuItem[] {
    this.setRole(role);
    return this.userMenuItems();
  }

  openNectaManual(): void {
    if(environment.brandKey === 'necta') {
      window.open('https://doku.necta.at/manual', '_blank', 'noopener,noreferrer');
    }
  }
}
